'use client';

import { c as _c } from "react/compiler-runtime";
import { isEqual } from 'es-toolkit';
import { useAtom, useAtomValue } from 'jotai';
import { useQueryState } from 'nuqs';
import { Suspense } from 'react';
import { useImmer } from 'use-immer';
import type { FileType } from '@/atoms/file-types';
import type { CheckedState } from '@/types/utils';
import { fileTypesAtom } from '@/atoms/file-types';
import { Button } from '@/components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle, DrawerTrigger } from '@/components/ui/drawer';
import { Separator } from '@/components/ui/separator';
import { Toggle } from '@/components/ui/toggle';
import { mediaQueryFamily } from '@sub-vocab/utils/atoms';
function FileSettingsContent(t0) {
  const $ = _c(10);
  const {
    className,
    fileTypes,
    onFileTypeChange: setFileType
  } = t0;
  let t1;
  if ($[0] !== fileTypes || $[1] !== setFileType) {
    let t2;
    if ($[3] !== setFileType) {
      t2 = fileType => {
        const {
          type,
          checked
        } = fileType;
        return <div key={type} className="flex items-center space-x-2"><Toggle pressed={checked} variant="outline" aria-label="Regular expression" className="size-fit min-w-8.75 px-1.5 py-1 text-muted-foreground" onPressedChange={pressed => {
            setFileType(fileType, pressed);
          }}>{fileType.type}</Toggle></div>;
      };
      $[3] = setFileType;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = fileTypes.map(t2);
    $[0] = fileTypes;
    $[1] = setFileType;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <div className="flex flex-wrap gap-x-3 gap-y-2">{t1}</div>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== className || $[8] !== t2) {
    t3 = <div className={className}>{t2}</div>;
    $[7] = className;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
const FILE_SETTINGS_TITLE = `Select File Types`;
const FILE_SETTINGS_DESCRIPTION = `Choose the file types you want to include for text input.`;
const Trigger = <Button variant="outline" className="size-8 p-0" aria-label="Settings-Trigger" suppressHydrationWarning>
    <svg className="icon-[lucide--cog] size-3.5" />
  </Button>;
export function Settings() {
  const $ = _c(50);
  const isMdScreen = useAtomValue(mediaQueryFamily.useA("(min-width: 768px)"));
  const [popup, setPopup] = useQueryState("popup");
  const open = popup === "file-settings";
  const [fileTypes, setFileTypes] = useAtom(fileTypesAtom);
  const [fileTypesInterim, setFileTypesInterim] = useImmer(fileTypes);
  let t0;
  if ($[0] !== setFileTypesInterim) {
    t0 = function handleFileTypeChange(t1, checkedState) {
      const {
        type
      } = t1;
      setFileTypesInterim(draft => {
        const fileType = draft.find(ft => ft.type === type);
        if (fileType) {
          fileType.checked = checkedState === true;
        }
      });
    };
    $[0] = setFileTypesInterim;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const handleFileTypeChange = t0;
  let t1;
  if ($[2] !== fileTypesInterim || $[3] !== setFileTypes || $[4] !== setPopup) {
    t1 = function save() {
      setFileTypes(fileTypesInterim);
      setPopup(null);
    };
    $[2] = fileTypesInterim;
    $[3] = setFileTypes;
    $[4] = setPopup;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const save = t1;
  let t2;
  if ($[6] !== fileTypes || $[7] !== fileTypesInterim) {
    t2 = isEqual(fileTypes, fileTypesInterim);
    $[6] = fileTypes;
    $[7] = fileTypesInterim;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const settingsUnchanged = t2;
  let t3;
  if ($[9] !== fileTypes || $[10] !== setFileTypesInterim || $[11] !== setPopup) {
    t3 = function handleOpenChange(open_0) {
      if (open_0) {
        setPopup("file-settings");
      } else {
        setPopup(null);
        setFileTypesInterim(fileTypes);
      }
    };
    $[9] = fileTypes;
    $[10] = setFileTypesInterim;
    $[11] = setPopup;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const handleOpenChange = t3;
  if (isMdScreen) {
    let t4;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <DialogTrigger aria-label="file-settings" render={Trigger} />;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    let t5;
    let t6;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <DialogHeader className="pb-2"><DialogTitle>{FILE_SETTINGS_TITLE}</DialogTitle><DialogDescription>{FILE_SETTINGS_DESCRIPTION}</DialogDescription></DialogHeader>;
      t6 = <Separator />;
      $[14] = t5;
      $[15] = t6;
    } else {
      t5 = $[14];
      t6 = $[15];
    }
    let t7;
    if ($[16] !== fileTypesInterim || $[17] !== handleFileTypeChange) {
      t7 = <div className="-ml-1 size-full overflow-y-scroll px-0.5 py-3 pl-1"><FileSettingsContent fileTypes={fileTypesInterim} onFileTypeChange={handleFileTypeChange} /></div>;
      $[16] = fileTypesInterim;
      $[17] = handleFileTypeChange;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    let t8;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <Separator />;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    let t9;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <DialogClose render={<Button variant="outline">Cancel</Button>} />;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    let t10;
    if ($[21] !== save || $[22] !== settingsUnchanged) {
      t10 = <DialogFooter className="pt-3">{t9}<Button type="submit" disabled={settingsUnchanged} onClick={save}>Save changes</Button></DialogFooter>;
      $[21] = save;
      $[22] = settingsUnchanged;
      $[23] = t10;
    } else {
      t10 = $[23];
    }
    let t11;
    if ($[24] !== t10 || $[25] !== t7) {
      t11 = <DialogContent className="flex max-h-[calc(100vh-1rem)] flex-col gap-0 sm:max-w-106.25">{t5}{t6}{t7}{t8}{t10}</DialogContent>;
      $[24] = t10;
      $[25] = t7;
      $[26] = t11;
    } else {
      t11 = $[26];
    }
    let t12;
    if ($[27] !== handleOpenChange || $[28] !== open || $[29] !== t11) {
      t12 = <Dialog open={open} onOpenChange={handleOpenChange}>{t4}{t11}</Dialog>;
      $[27] = handleOpenChange;
      $[28] = open;
      $[29] = t11;
      $[30] = t12;
    } else {
      t12 = $[30];
    }
    return t12;
  }
  let t4;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <DrawerTrigger asChild={true}>{Trigger}</DrawerTrigger>;
    $[31] = t4;
  } else {
    t4 = $[31];
  }
  let t5;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="w-full border-b pt-1 pb-3 text-lg"><DrawerTitle className="m-auto w-fit text-lg leading-none font-semibold">{FILE_SETTINGS_TITLE}</DrawerTitle></div>;
    $[32] = t5;
  } else {
    t5 = $[32];
  }
  let t6;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <DrawerHeader className="px-0 text-left"><DrawerDescription>{FILE_SETTINGS_DESCRIPTION}</DrawerDescription></DrawerHeader>;
    $[33] = t6;
  } else {
    t6 = $[33];
  }
  let t7;
  if ($[34] !== fileTypesInterim || $[35] !== handleFileTypeChange) {
    t7 = <div className="size-full overflow-y-scroll px-6 pb-4"><div className="mx-auto max-w-106.25">{t6}<FileSettingsContent fileTypes={fileTypesInterim} onFileTypeChange={handleFileTypeChange} /></div></div>;
    $[34] = fileTypesInterim;
    $[35] = handleFileTypeChange;
    $[36] = t7;
  } else {
    t7 = $[36];
  }
  let t8;
  if ($[37] !== save || $[38] !== settingsUnchanged) {
    t8 = <Button type="submit" disabled={settingsUnchanged} onClick={save}>Save changes</Button>;
    $[37] = save;
    $[38] = settingsUnchanged;
    $[39] = t8;
  } else {
    t8 = $[39];
  }
  let t9;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <DrawerClose asChild={true}><Button variant="outline">Cancel</Button></DrawerClose>;
    $[40] = t9;
  } else {
    t9 = $[40];
  }
  let t10;
  if ($[41] !== t8) {
    t10 = <DrawerFooter className="border-t pt-2">{t8}{t9}</DrawerFooter>;
    $[41] = t8;
    $[42] = t10;
  } else {
    t10 = $[42];
  }
  let t11;
  if ($[43] !== t10 || $[44] !== t7) {
    t11 = <DrawerContent>{t5}{t7}{t10}</DrawerContent>;
    $[43] = t10;
    $[44] = t7;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  let t12;
  if ($[46] !== handleOpenChange || $[47] !== open || $[48] !== t11) {
    t12 = <Drawer open={open} shouldScaleBackground={true} onOpenChange={handleOpenChange}>{t4}{t11}</Drawer>;
    $[46] = handleOpenChange;
    $[47] = open;
    $[48] = t11;
    $[49] = t12;
  } else {
    t12 = $[49];
  }
  return t12;
}
export function FileSettings() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Suspense><Settings /></Suspense>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}